import React from 'react';
import { getPreIpoDetails, PreIpoApiResponse, submitPreIpoInterest } from './usePreIpo';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import { KeyboardArrowDown } from '@mui/icons-material';
import SelectClient from '../SelectClient';
import ClientDrawer from '../../Clients/ClientDrawer/ClientDrawer';
import { InfoOutlinedIcon } from '../../../assets/mui';
import useGetPartnerProfile from '../../../hooks/useGetPartnerProfile';
import { toast } from 'react-hot-toast';

const PreIpo = () => {
  const [res, setRes] = React.useState<PreIpoApiResponse | null>(null);
  // Calculator state
  const [selectedDeal, setSelectedDeal] = React.useState(null);
  const [clientPrice, setClientPrice] = React.useState('');
  const [numberOfShares, setNumberOfShares] = React.useState('');
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [agreed, setAgreed] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { data: partnerDetails } = useGetPartnerProfile();

  React.useEffect(() => {
    const fetchDetails = async () => {
      const result = await getPreIpoDetails();
      setRes(result);
    };
    fetchDetails();
  }, []);

  const amountFunc = (amount) => {
    return amount ? (
      <div className="flex flex-row items-center gap-[2px]">
        <span className="text-stone-400 text-sm font-medium">₹</span>
        <div className="text-slate-600 text-lg font-medium">{amount}</div>
      </div>
    ) : (
      <span className="text-slate-600 text-lg font-medium">-</span>
    );
  };
  // Helper for formatting numbers in Indian system
  const formatIN = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return num.toLocaleString('en-IN');
  };
  // Helper for calculator fields
  const deals = (res?.data?.deals || []).filter((deal) => deal.isLive);
  const sharesPerLot = selectedDeal ? Number(selectedDeal.lotSize) : 0;
  const partnerPrice = selectedDeal ? Number(selectedDeal.partnerPricePerShare) : 0;
  const minShares = selectedDeal ? Number(selectedDeal.lotSize) : 0;

  // Parse client price and number of shares early for use in calculations
  const clientPriceNum = Number(clientPrice);
  const numberOfSharesNum = Number(numberOfShares);

  // Calculate minimum shares required
  const minSharesRequired = minShares || 1;

  // Set default values when deal changes
  React.useEffect(() => {
    if (selectedDeal) {
      setClientPrice(String(partnerPrice));
      setNumberOfShares(String(minSharesRequired));
    }
  }, [selectedDeal]);

  // Validation
  const clientPriceError = clientPrice && clientPriceNum < partnerPrice;
  const numberOfSharesError = numberOfShares && numberOfSharesNum < minSharesRequired;
  const hasClientPrice = clientPrice && clientPrice.trim() !== '';
  const hasNumberOfShares = numberOfShares && numberOfShares.trim() !== '';
  const hasSelectedClient = selectedClient !== null;
  const isValid =
    selectedDeal &&
    hasClientPrice &&
    !isNaN(clientPriceNum) &&
    clientPriceNum >= partnerPrice &&
    hasNumberOfShares &&
    !isNaN(numberOfSharesNum) &&
    numberOfSharesNum >= minSharesRequired &&
    sharesPerLot > 0;

  // Calculator logic - calculate total investment based on number of shares
  const totalShares = isValid ? numberOfSharesNum : 0;
  const totalInvestment = isValid ? totalShares * clientPriceNum : 0;
  const commission = isValid ? totalShares * (clientPriceNum - partnerPrice) : 0;
  // Create data object for backend submission

  const submissionData = isValid
    ? {
        partnerName:
          partnerDetails?.data?.profile?.firstName +
            ' ' +
            partnerDetails?.data?.profile?.lastName || '',
        dealId: String(selectedDeal.deal_id),
        dealBrandName: selectedDeal.brandName,
        dealCompanyName: selectedDeal.companyName,
        dealIsin: selectedDeal.isin,
        partnerPrice: partnerPrice,
        clientPrice: clientPriceNum,
        investmentAmount: totalInvestment,
        lotSize: sharesPerLot,
        numberOfLots: Math.floor(numberOfSharesNum / sharesPerLot),
        totalInvestment: totalInvestment,
        commission: commission,
        remainingAmount: totalInvestment - commission,
        partnerEmail: partnerDetails?.data?.profile?.email || '',
        partnerPhone: partnerDetails?.data?.profile?.phone || '',
        clientId: selectedClient?.clientPublicId || '',
        clientName: selectedClient?.name || '',
        clientEmail: selectedClient?.email || '',
        clientPhone: selectedClient?.phone || '',
        isAgreed: agreed,
        timestamp: new Date().toISOString(),
      }
    : null;

  // Input change handlers to allow only numbers
  const handleClientPriceChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      // Limit to 10 digits before the decimal
      const [intPart, decPart] = value.split('.');
      if (intPart.length <= 10) {
        setClientPrice(value);
      }
    }
  };
  const handleNumberOfSharesChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      // Only allow whole numbers for shares
      if (value.length <= 10) {
        setNumberOfShares(value);
      }
    }
  };

  return (
    <div className="my-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row gap-8 min-h-screen relative">
        {/* Left section (60%) */}
        <div className="w-full md:w-3/5 overflow-y-auto">
          <div className="">
            <div className="flex items-center gap-4 mb-3">
              <h1 className="text-xl font-semibold text-[#152420]">Pre IPO</h1>
              <span className="bg-[#E6F0E8] text-green-800 text-[10px] font-semibold rounded-md px-2 py-1 tracking-wide">
                NEW
              </span>
            </div>
            <p className="text-base text-stone-500 leading-relaxed mb-3">
              Pre-IPO, or pre-initial public offering, refers to the period before a company offers
              its shares to the public for the first time through an IPO. During this phase,
              companies may offer their shares to a select group of investors, often institutions,
              to raise capital before going public.
            </p>
            <p className="text-xs italic text-stone-400 mt-2">
              *Price is negotiable subject to volume and final execution terms.
            </p>
          </div>
          {/* Available deals section */}
          {deals.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-[#23272F] mb-3">Available deals</h2>
              <div className="flex flex-col gap-4">
                {deals.map((deal) => (
                  <div
                    key={deal.deal_id}
                    className={`bg-white rounded-xl border overflow-hidden shadow-sm cursor-pointer transition-all duration-150 ${
                      selectedDeal && selectedDeal.deal_id === deal.deal_id
                        ? 'border-zinc-600 ring-2 ring-zinc-100'
                        : 'border-zinc-200'
                    }`}
                    onClick={() => setSelectedDeal(deal)}
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setSelectedDeal(deal);
                    }}
                    aria-label={`Select deal ${deal.brandName}`}
                  >
                    <div className="flex items-center gap-4 px-6 py-5 bg-white">
                      <img
                        src={deal.primaryLogo?.formats?.thumbnail?.url || deal.primaryLogo?.url}
                        alt={deal.brandName}
                        className="w-12 h-12 rounded object-contain bg-white border border-zinc-200"
                      />
                      <div>
                        <div className="text-lg font-semibold text-[#23272F]">{deal.brandName}</div>
                        <div className="text-sm text-[#6B6B6B]">{deal.companyName}</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center bg-[#F5F5F3] px-6 py-4 border-t border-zinc-200">
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-stone-600 tracking-wide mb-1">
                          PARTNER'S PRICE
                        </span>
                        <span className="text-base font-semibold text-[#23272F]">
                          ₹{deal.partnerPricePerShare}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-stone-600 tracking-wide mb-1">
                          SHARES PER LOT
                        </span>
                        <span className="text-base font-semibold text-[#23272F]">
                          {deal.lotSize}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-stone-600 tracking-wide mb-1">
                          MIN INVESTMENT
                        </span>
                        <span className="text-base font-semibold text-green-700">
                          {deal.lotSize && deal.partnerPricePerShare ? (
                            <>
                              ₹{' '}
                              {(
                                Number(deal.lotSize) * Number(deal.partnerPricePerShare)
                              ).toLocaleString()}
                            </>
                          ) : (
                            '-'
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Right section (40%) - Calculator UI */}
        <div className="w-full md:w-2/5 sticky top-24 h-fit">
          <div>
            <div className="mb-8 border-stone-300 border rounded-lg">
              {/* Select Deal */}
              <Select
                displayEmpty
                value={selectedDeal ? String(selectedDeal.deal_id) : ''}
                onChange={(e) => {
                  const deal = deals.find((d) => String(d.deal_id) === e.target.value);
                  setSelectedDeal(deal || null);
                }}
                IconComponent={KeyboardArrowDown}
                input={
                  <OutlinedInput
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#D0D5DD',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#D0D5DD',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#D0D5DD',
                      },
                      backgroundColor: 'white',
                      // border: '1px',
                    }}
                  />
                }
                className="w-full bg-white !rounded-lg h-[52px]"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D0D5DD',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D0D5DD',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D0D5DD',
                  },
                  backgroundColor: 'white',
                  // border: '0 1px',
                  boxShadow: '0px 2px 8px 0px #0000001A',
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span className="text-stone-900 font-medium text-base">Select Deal</span>
                    );
                  }
                  const deal = deals.find((d) => String(d.deal_id) === selected);
                  if (!deal) return <span className="text-gray-400">Select Deal</span>;
                  return (
                    // <div className="flex items-center gap-3">
                    //   <img
                    //     src={deal.primaryLogo?.formats?.thumbnail?.url || deal.primaryLogo?.url}
                    //     alt={deal.brandName}
                    //     className="w-7 h-7 rounded object-contain bg-white border border-zinc-200"
                    //   />
                    //   <div>
                    //     <div className="text-sm font-medium text-[#23272F]">{deal.brandName}</div>
                    //     <div className="text-xs text-[#6B6B6B]">{deal.companyName}</div>
                    //   </div>
                    // </div>
                    <div className="text-sm font-medium text-[#23272F]">{deal.brandName}</div>
                  );
                }}
                MenuProps={{
                  MenuListProps: {
                    className: 'max-h-80 overflow-auto',
                  },
                }}
              >
                {deals.map((deal) => (
                  <MenuItem key={deal.deal_id} value={String(deal.deal_id)}>
                    <div className="flex items-center gap-3">
                      <img
                        src={deal.primaryLogo?.formats?.thumbnail?.url || deal.primaryLogo?.url}
                        alt={deal.brandName}
                        className="w-7 h-7 rounded object-contain bg-white border border-zinc-200"
                      />
                      <div>
                        <div className="text-sm font-medium text-[#23272F]">{deal.brandName}</div>
                        <div className="text-xs text-[#6B6B6B]">{deal.companyName}</div>
                      </div>
                    </div>
                  </MenuItem>
                ))}
              </Select>

              {/* Partner's Price */}

              <div className="flex items-center p-4 bg-[#f6f6f1] rounded-b-lg">
                <span className="text-sm text-slate-600 w-[155px]">Partner's Price</span>
                {amountFunc(partnerPrice)}
              </div>
            </div>

            <div
              className={`border border-stone-300 bg-white rounded-lg mb-8 ${
                numberOfSharesError ? 'pb-1' : 'pb-0'
              }`}
            >
              {/* Client's Price */}
              <div className={`flex items-center p-4 ${clientPriceError ? 'pb-0' : 'pb-4'}`}>
                <span className="text-sm text-slate-600 w-[155px] flex-shrink-0">
                  Client's price
                </span>
                <span className="text-stone-400 text-sm font-medium flex-shrink-0">₹</span>
                <input
                  className="flex-1 min-w-0 outline-none bg-transparent text-lg font-medium text-slate-600 ml-1"
                  type="text"
                  placeholder=""
                  value={clientPrice}
                  min={partnerPrice}
                  onChange={handleClientPriceChange}
                />
              </div>
              {clientPriceError && (
                <span
                  className={`text-xs text-red-500 ml-4 ${numberOfSharesError ? 'pb-2' : 'pb-4'}`}
                >
                  Client price cannot be less than partner price
                </span>
              )}
              {/* Number of Shares */}

              <div
                className={`flex items-center border-t border-stone-300 p-4 ${
                  numberOfSharesError ? 'pb-0' : 'pb-4'
                }`}
              >
                <span className="text-sm text-slate-600 w-[155px] flex-shrink-0">
                  Number of Shares
                </span>
                <input
                  className="flex-1 min-w-0 outline-none bg-transparent text-lg font-medium text-slate-600"
                  type="text"
                  placeholder=""
                  value={numberOfShares}
                  min={minSharesRequired}
                  onChange={handleNumberOfSharesChange}
                />
              </div>
              {numberOfSharesError && (
                <span className="text-xs text-red-500 ml-4">
                  Number of shares cannot be less than minimum ({minSharesRequired})
                </span>
              )}
            </div>

            {/* Investment Amount & Commission */}
            <div className="mb-8 bg-[#f6f6f1] rounded-lg border-stone-300 border">
              <div className="flex items-center p-4 ">
                <span className="text-sm text-slate-600 w-[155px]">Investment Amount</span>
                <span className="font-medium text-slate-800">
                  {isValid ? `₹${formatIN(totalInvestment)}` : '-'}
                </span>
              </div>
              <div className="flex items-center p-4 border-t border-stone-300">
                <span className="text-sm text-slate-600 w-[155px]">Your Commission</span>
                <span className="font-medium text-slate-800 min-w-[60px]">
                  {isValid ? `₹${formatIN(commission)}` : '-'}
                </span>
              </div>
            </div>
            {/* Select Client */}
            <div className="mb-8">
              <SelectClient
                client={selectedClient}
                setClient={setSelectedClient}
                showAddNewClient={true}
              />
            </div>
            {/* Terms & Conditions */}
            <div className="flex items-start mb-8">
              <input
                id="agree"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="agree" className="ml-2 text-xs text-gray-500">
                By proceeding you agree to our{' '}
                <a href="/terms" className="underline" target="_blank" rel="noopener noreferrer">
                  Terms & Conditions
                </a>
              </label>
            </div>
            {/* I'm Interested Button */}
            <button
              className="w-full bg-green-700 text-white py-2 rounded-lg font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                !agreed ||
                !hasClientPrice ||
                !hasNumberOfShares ||
                !hasSelectedClient ||
                clientPriceError ||
                numberOfSharesError ||
                isSubmitting
              }
              onClick={async () => {
                if (submissionData) {
                  try {
                    setIsSubmitting(true);
                    await submitPreIpoInterest(submissionData);
                    toast.success('Pre-IPO interest submitted successfully');
                  } catch (error) {
                    toast.error('Error submitting Pre-IPO interest', {
                      duration: 3000,
                    });
                  } finally {
                    setIsSubmitting(false);
                  }
                }
              }}
            >
              {isSubmitting ? 'Submitting...' : "I'm Interested"}
            </button>
          </div>
        </div>
      </div>
      {/* Add new clients */}
      <ClientDrawer />
    </div>
  );
};

export default PreIpo;
